import { useState, useEffect, useCallback, useRef } from 'react';
import styles from './PainelImpressao.module.css';
import { MdPrint, MdComputer, MdPhoneAndroid, MdFileDownload, MdRefresh, MdLinkOff, MdReplay, MdCheckCircle, MdErrorOutline } from 'react-icons/md';

const API_URL = process.env.REACT_APP_API_URL;
// Mantém "online/offline" e o histórico atualizados sem precisar recarregar.
const INTERVALO_ATUALIZACAO_MS = 5000;

const ROTULO_ORIGEM = {
  whatsapp: 'WhatsApp AI',
  ifood: 'iFood',
  app: 'App / Site',
  'balcão': 'Balcão',
  mesa_qrcode: 'Mesa (QR Code)',
  garcom: 'Garçom (mesa)'
};

const ROTULO_CAMPO = {
  estabelecimento: 'Nome do estabelecimento',
  horario: 'Data e horário',
  cliente: 'Nome do cliente',
  adicionais: 'Adicionais e sabores',
  observacoes: 'Observações',
  endereco: 'Endereço de entrega',
  pagamento: 'Forma de pagamento',
  precos: 'Preço de cada item',
  total: 'Total'
};

const ROTULO_STATUS = {
  pendente: 'Na fila',
  reservado: 'Imprimindo',
  impresso: 'Impresso',
  falhou: 'Não imprimiu',
  incerto: 'Conferir',
  expirado: 'Expirou',
  cancelado: 'Cancelado'
};

// Mensagens pensadas pra quem não é técnico: o que aconteceu e o que fazer.
const ROTULO_ERRO = {
  IMPRESSORA_OFFLINE: 'Impressora desligada, fora de alcance ou em uso por outro aplicativo. Vamos tentar de novo.',
  IMPRESSORA_OCUPADA: 'Impressora em uso por outro aplicativo (ex.: app de delivery). Vamos tentar de novo.',
  SEM_PAPEL: 'Impressora sem papel.',
  SEM_PERMISSAO: 'Falta permissão no aparelho (Bluetooth/dispositivos próximos). Abra o conector e conceda.',
  BLUETOOTH_DESLIGADO: 'Bluetooth desligado no aparelho.',
  PROTOCOLO_INCOMPATIVEL: 'Esta impressora não é compatível com este modo de impressão. Troque o modo ou a impressora.',
  IMPRESSORA_NAO_ENCONTRADA: 'A impressora não está mais instalada/pareada no aparelho.',
  TEMPO_ESGOTADO: 'O aparelho não respondeu a tempo.'
};

const TIPO_CONEXAO = {
  driver_windows: 'Windows',
  usb: 'USB',
  bluetooth: 'Bluetooth',
  bluetooth_le: 'Bluetooth LE',
  rede: 'Rede',
  simulador: 'Simulador'
};

function cabecalhosAutenticados() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

async function chamar(metodo, rota, corpo) {
  const res = await fetch(`${API_URL}${rota}`, {
    method: metodo,
    headers: cabecalhosAutenticados(),
    body: corpo ? JSON.stringify(corpo) : undefined
  });
  const dados = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(dados.error || dados.erro || 'Não foi possível concluir.');
  return dados;
}

function formatarHora(data) {
  if (!data) return '—';
  return new Date(data).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// Configuração da impressão automática (Speed Order Print) de UM
// estabelecimento. Usado pelo painel do administrador da loja
// (modules/administrador/pages/ImpressaoAutomatica.jsx) e pelo SpeedHub, onde
// o master gerencia qualquer loja (modules/speedhub/pages/GerenciarEstabelecimento.jsx).
// O backend confere se quem está logado pode mexer nesse estabelecimento.
//
// "embutido": desenha o próprio fundo escuro, pra ficar legível dentro de uma
// tela com outro tema (SpeedHub).
function PainelImpressao({ idEstabelecimento, embutido = false }) {
  const estabelecimentoId = idEstabelecimento;

  const [carregando, setCarregando] = useState(true);
  const [erroGeral, setErroGeral] = useState('');
  const [config, setConfig] = useState(null);
  const [origensDisponiveis, setOrigensDisponiveis] = useState([]);
  const [camposLayout, setCamposLayout] = useState([]);
  const [downloads, setDownloads] = useState({});
  const [dispositivos, setDispositivos] = useState([]);
  const [trabalhos, setTrabalhos] = useState([]);

  const [plataforma, setPlataforma] = useState('windows');
  const [codigo, setCodigo] = useState(null); // { codigo, expira_em }
  const [segundosRestantes, setSegundosRestantes] = useState(0);
  const [gerandoCodigo, setGerandoCodigo] = useState(false);
  const [testando, setTestando] = useState(null);
  const [aviso, setAviso] = useState('');
  const avisoTimer = useRef(null);

  const mostrarAviso = (texto) => {
    setAviso(texto);
    clearTimeout(avisoTimer.current);
    avisoTimer.current = setTimeout(() => setAviso(''), 6000);
  };

  const buscarConfiguracao = useCallback(async () => {
    const dados = await chamar('GET', `/print/settings?id_estabelecimento=${estabelecimentoId}`);
    setConfig(dados.configuracao);
    setOrigensDisponiveis(dados.origens_disponiveis || []);
    setCamposLayout(dados.campos_layout || []);
    setDownloads(dados.downloads || {});
  }, [estabelecimentoId]);

  const buscarDispositivosEHistorico = useCallback(async () => {
    const [d, t] = await Promise.all([
      chamar('GET', `/print/devices?id_estabelecimento=${estabelecimentoId}`),
      chamar('GET', `/print/jobs?id_estabelecimento=${estabelecimentoId}&limite=30`)
    ]);
    setDispositivos(d.dispositivos || []);
    setTrabalhos(t.trabalhos || []);
  }, [estabelecimentoId]);

  useEffect(() => {
    if (!estabelecimentoId) return undefined;
    let ativo = true;

    (async () => {
      try {
        await Promise.all([buscarConfiguracao(), buscarDispositivosEHistorico()]);
      } catch (error) {
        if (ativo) setErroGeral(error.message);
      } finally {
        if (ativo) setCarregando(false);
      }
    })();

    const intervalo = setInterval(() => {
      buscarDispositivosEHistorico().catch(() => { /* tenta de novo no próximo ciclo */ });
    }, INTERVALO_ATUALIZACAO_MS);

    return () => { ativo = false; clearInterval(intervalo); clearTimeout(avisoTimer.current); };
  }, [estabelecimentoId, buscarConfiguracao, buscarDispositivosEHistorico]);

  // Contagem regressiva do código de vinculação.
  useEffect(() => {
    if (!codigo) return undefined;
    const atualizar = () => {
      const restam = Math.max(0, Math.round((new Date(codigo.expira_em).getTime() - Date.now()) / 1000));
      setSegundosRestantes(restam);
      if (restam === 0) setCodigo(null);
    };
    atualizar();
    const intervalo = setInterval(atualizar, 1000);
    return () => clearInterval(intervalo);
  }, [codigo]);

  // Aparelho novo apareceu na lista: o código já foi usado, some da tela.
  const totalDispositivos = dispositivos.length;
  const totalAnterior = useRef(totalDispositivos);
  useEffect(() => {
    if (totalDispositivos > totalAnterior.current && codigo) {
      setCodigo(null);
      mostrarAviso('Aparelho vinculado! Agora escolha a impressora logo abaixo.');
    }
    totalAnterior.current = totalDispositivos;
  }, [totalDispositivos, codigo]);

  const salvarConfiguracao = async (alteracao) => {
    const anterior = config;
    setConfig({ ...config, ...alteracao }); // resposta imediata na tela
    try {
      const dados = await chamar('PUT', '/print/settings', { id_estabelecimento: estabelecimentoId, ...alteracao });
      setConfig(dados.configuracao);
    } catch (error) {
      setConfig(anterior);
      alert('Erro ao salvar: ' + error.message);
    }
  };

  const alternarOrigem = (origem) => {
    const atuais = config.origens_habilitadas || [];
    const novas = atuais.includes(origem) ? atuais.filter((o) => o !== origem) : [...atuais, origem];
    salvarConfiguracao({ origens_habilitadas: novas });
  };

  const alternarCampo = (campo) => {
    salvarConfiguracao({ layout: { ...config.layout, [campo]: !config.layout[campo] } });
  };

  const gerarCodigo = async () => {
    setGerandoCodigo(true);
    try {
      setCodigo(await chamar('POST', '/print/pairing-code', { id_estabelecimento: estabelecimentoId }));
    } catch (error) {
      alert('Erro ao gerar código: ' + error.message);
    } finally {
      setGerandoCodigo(false);
    }
  };

  const atualizarImpressora = async (impressora, alteracao) => {
    try {
      await chamar('PUT', `/print/printers/${impressora.id}`, { id_estabelecimento: estabelecimentoId, ...alteracao });
      await buscarDispositivosEHistorico();
    } catch (error) {
      alert('Erro ao salvar impressora: ' + error.message);
    }
  };

  const imprimirTeste = async (impressora) => {
    setTestando(impressora.id);
    try {
      await chamar('POST', '/print/test', { id_estabelecimento: estabelecimentoId, printer_id: impressora.id });
      mostrarAviso(`Comanda de teste enviada para "${impressora.nome}". Acompanhe no histórico abaixo.`);
      await buscarDispositivosEHistorico();
    } catch (error) {
      alert('Erro ao imprimir teste: ' + error.message);
    } finally {
      setTestando(null);
    }
  };

  const desvincular = async (dispositivo) => {
    if (!window.confirm(`Desvincular "${dispositivo.nome}"? Ele para de imprimir os pedidos na hora.`)) return;
    try {
      await chamar('DELETE', `/print/devices/${dispositivo.id}?id_estabelecimento=${estabelecimentoId}`);
      await Promise.all([buscarConfiguracao(), buscarDispositivosEHistorico()]);
    } catch (error) {
      alert('Erro ao desvincular: ' + error.message);
    }
  };

  const reimprimir = async (trabalho) => {
    try {
      await chamar('POST', `/print/jobs/${trabalho.id}/reprint`, { id_estabelecimento: estabelecimentoId });
      mostrarAviso('Reimpressão enviada.');
      await buscarDispositivosEHistorico();
    } catch (error) {
      alert('Erro ao reimprimir: ' + error.message);
    }
  };

  const impressoraPadrao = dispositivos.flatMap((d) => d.impressoras.map((i) => ({ ...i, dispositivo: d }))).find((i) => i.id === config?.impressora_padrao);
  const linkDownload = downloads[plataforma];

  // Uma frase que diz se está tudo certo ou o que falta.
  const situacao = (() => {
    if (!config) return null;
    if (dispositivos.length === 0) return { ok: false, texto: 'Nenhum aparelho vinculado ainda. Siga o passo 1.' };
    if (!impressoraPadrao) return { ok: false, texto: 'Escolha qual impressora imprime os pedidos (passo 2).' };
    if (!impressoraPadrao.dispositivo.online) return { ok: false, texto: `O aparelho "${impressoraPadrao.dispositivo.nome}" está offline — os pedidos ficam na fila até ele voltar.` };
    if (!config.impressao_automatica) return { ok: false, texto: 'Tudo pronto. Ligue a impressão automática aqui em cima.' };
    if ((config.origens_habilitadas || []).length === 0) return { ok: false, texto: 'Escolha de onde vêm os pedidos que devem imprimir (passo 3).' };
    return { ok: true, texto: `Funcionando: os pedidos saem em "${impressoraPadrao.nome}".` };
  })();

  return (
    <div className={`${styles.painel} ${embutido ? styles.painelEmbutido : ''}`}>
      <div className={styles.barraTopo}>
        <span className={styles.barraTopoTexto}>Imprimir os pedidos automaticamente</span>
        {config && (
          <label className={styles.interruptor}>
            <input
              type="checkbox"
              checked={config.impressao_automatica}
              onChange={(e) => salvarConfiguracao({ impressao_automatica: e.target.checked })}
            />
            <span className={styles.interruptorTrilho} />
            <span className={styles.interruptorTexto}>{config.impressao_automatica ? 'Ligada' : 'Desligada'}</span>
          </label>
        )}
      </div>

      {carregando && <p className={styles.mensagemVazia}>Carregando...</p>}
      {erroGeral && <p className={styles.mensagemErro}>{erroGeral}</p>}
      {aviso && <p className={styles.aviso}>{aviso}</p>}

      {config && situacao && (
        <div className={`${styles.situacao} ${situacao.ok ? styles.situacaoOk : styles.situacaoPendente}`}>
          {situacao.ok ? <MdCheckCircle /> : <MdErrorOutline />}
          <span>{situacao.texto}</span>
        </div>
      )}

      {config && (
        <>
          {/* ---------- Passo 1: conectar um aparelho ---------- */}
          <section className={styles.cartao}>
            <h3 className={styles.cartaoTitulo}><span className={styles.passo}>1</span> Conectar o aparelho que fica ligado à impressora</h3>
            <div className={styles.seletorPlataforma}>
              <button
                type="button"
                className={`${styles.opcaoPlataforma} ${plataforma === 'windows' ? styles.opcaoAtiva : ''}`}
                onClick={() => setPlataforma('windows')}
              >
                <MdComputer /> <span>Computador Windows</span>
              </button>
              <button
                type="button"
                className={`${styles.opcaoPlataforma} ${plataforma === 'android' ? styles.opcaoAtiva : ''}`}
                onClick={() => setPlataforma('android')}
              >
                <MdPhoneAndroid /> <span>Celular ou tablet Android</span>
              </button>
            </div>

            <ol className={styles.listaPassos}>
              <li>
                {plataforma === 'windows' ? 'Baixe e instale o Speed Order Print neste computador.' : 'Instale o Speed Order Print no celular ou tablet.'}{' '}
                {linkDownload ? (
                  <a className={styles.linkDownload} href={linkDownload} target="_blank" rel="noopener noreferrer"><MdFileDownload /> Baixar</a>
                ) : (
                  <span className={styles.textoMuted}>(peça o instalador ao suporte do Speed Order)</span>
                )}
              </li>
              <li>Clique em <b>Gerar código</b> e digite o código no aplicativo.</li>
              {plataforma === 'android' && <li>No aplicativo, permita o uso do Bluetooth e as notificações, e escolha a impressora.</li>}
            </ol>

            {codigo ? (
              <div className={styles.caixaCodigo}>
                <span className={styles.codigo}>{codigo.codigo}</span>
                <span className={styles.textoMuted}>
                  Vale por {Math.floor(segundosRestantes / 60)}:{String(segundosRestantes % 60).padStart(2, '0')} e só pode ser usado uma vez.
                </span>
              </div>
            ) : (
              <button type="button" className={styles.btnPrimario} onClick={gerarCodigo} disabled={gerandoCodigo}>
                {gerandoCodigo ? 'Gerando...' : 'Gerar código'}
              </button>
            )}
          </section>

          {/* ---------- Passo 2: aparelhos e impressoras ---------- */}
          <section className={styles.cartao}>
            <h3 className={styles.cartaoTitulo}><span className={styles.passo}>2</span> Escolher a impressora</h3>
            {dispositivos.length === 0 ? (
              <p className={styles.textoMuted}>Nenhum aparelho vinculado ainda.</p>
            ) : dispositivos.map((dispositivo) => (
              <div key={dispositivo.id} className={styles.dispositivo}>
                <div className={styles.dispositivoCabecalho}>
                  <span className={styles.dispositivoIcone}>{dispositivo.plataforma === 'android' ? <MdPhoneAndroid /> : <MdComputer />}</span>
                  <div className={styles.dispositivoTextos}>
                    <strong>{dispositivo.nome}</strong>
                    <span className={styles.textoMuted}>
                      {dispositivo.online ? 'Conectado agora' : `Visto por último: ${formatarHora(dispositivo.last_seen_at)}`}
                    </span>
                  </div>
                  <span className={dispositivo.online ? styles.badgeOnline : styles.badgeOffline}>{dispositivo.online ? 'Online' : 'Offline'}</span>
                  <button type="button" className={styles.btnIcone} onClick={() => desvincular(dispositivo)} title="Desvincular este aparelho"><MdLinkOff /></button>
                </div>

                {dispositivo.impressoras.length === 0 ? (
                  <p className={styles.textoMuted}>Este aparelho ainda não encontrou nenhuma impressora. Ligue a impressora e abra o aplicativo.</p>
                ) : dispositivo.impressoras.map((impressora) => (
                  <div key={impressora.id} className={`${styles.impressora} ${config.impressora_padrao === impressora.id ? styles.impressoraPadrao : ''}`}>
                    <label className={styles.impressoraEscolha}>
                      <input
                        type="radio"
                        name="impressora_padrao"
                        checked={config.impressora_padrao === impressora.id}
                        onChange={() => salvarConfiguracao({ impressora_padrao: impressora.id })}
                      />
                      <span>
                        <strong>{impressora.nome}</strong>
                        <span className={styles.textoMuted}> · {TIPO_CONEXAO[impressora.tipo_conexao] || impressora.tipo_conexao}{impressora.disponivel ? '' : ' · indisponível agora'}</span>
                      </span>
                    </label>

                    <div className={styles.impressoraOpcoes}>
                      <label className={styles.campo}>
                        <span className={styles.campoRotulo}>Papel</span>
                        <select className={styles.select} value={impressora.largura_papel} onChange={(e) => atualizarImpressora(impressora, { largura_papel: Number(e.target.value) })}>
                          <option value={80}>80 mm</option>
                          <option value={58}>58 mm</option>
                        </select>
                      </label>
                      {impressora.protocolo !== 'simulador' && impressora.tipo_conexao === 'driver_windows' && (
                        <label className={styles.campo}>
                          <span className={styles.campoRotulo}>Modo</span>
                          <select className={styles.select} value={impressora.protocolo} onChange={(e) => atualizarImpressora(impressora, { protocolo: e.target.value })}>
                            <option value="escpos">Térmica de cupom (ESC/POS)</option>
                            <option value="texto_driver">Impressora comum (driver do Windows)</option>
                          </select>
                        </label>
                      )}
                      {impressora.protocolo === 'escpos' && (
                        <label className={styles.campo}>
                          <span className={styles.campoRotulo}>Acentos</span>
                          <select className={styles.select} value={impressora.configuracao?.acentuacao || 'sem_acentos'} onChange={(e) => atualizarImpressora(impressora, { acentuacao: e.target.value })}>
                            <option value="sem_acentos">Sem acentos (funciona em todas)</option>
                            <option value="cp860">Com acentos — CP860</option>
                            <option value="cp850">Com acentos — CP850</option>
                            <option value="cp1252">Com acentos — Windows-1252</option>
                          </select>
                        </label>
                      )}
                      <button type="button" className={styles.btnSecundario} onClick={() => imprimirTeste(impressora)} disabled={testando === impressora.id}>
                        <MdPrint /> <span>{testando === impressora.id ? 'Enviando...' : 'Imprimir teste'}</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
            <p className={styles.textoMuted}>
              Se a comanda de teste sair com símbolos estranhos, use "Sem acentos". Se não sair nada numa impressora comum, troque o modo. Nem toda impressora é compatível — o histórico abaixo mostra o motivo quando não imprime.
            </p>
          </section>

          {/* ---------- Passo 3: o que imprimir ---------- */}
          <section className={styles.cartao}>
            <h3 className={styles.cartaoTitulo}><span className={styles.passo}>3</span> O que imprimir</h3>

            <span className={styles.campoRotulo}>Pedidos que imprimem sozinhos</span>
            <div className={styles.grupoOpcoes}>
              {origensDisponiveis.map((origem) => (
                <label key={origem} className={styles.opcao}>
                  <input type="checkbox" checked={(config.origens_habilitadas || []).includes(origem)} onChange={() => alternarOrigem(origem)} />
                  <span>{ROTULO_ORIGEM[origem] || origem}</span>
                </label>
              ))}
            </div>

            <div className={styles.linhaCampos}>
              <label className={styles.campo}>
                <span className={styles.campoRotulo}>Quantidade de vias</span>
                <select className={styles.select} value={config.quantidade_vias} onChange={(e) => salvarConfiguracao({ quantidade_vias: Number(e.target.value) })}>
                  {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} via{n > 1 ? 's' : ''}</option>)}
                </select>
              </label>
              <label className={styles.opcao}>
                <input type="checkbox" checked={config.corte_automatico} onChange={(e) => salvarConfiguracao({ corte_automatico: e.target.checked })} />
                <span>Cortar o papel ao final (se a impressora tiver guilhotina)</span>
              </label>
            </div>

            <span className={styles.campoRotulo}>O que aparece na comanda</span>
            <div className={styles.grupoOpcoes}>
              {camposLayout.map((campo) => (
                <label key={campo} className={styles.opcao}>
                  <input type="checkbox" checked={Boolean(config.layout?.[campo])} onChange={() => alternarCampo(campo)} />
                  <span>{ROTULO_CAMPO[campo] || campo}</span>
                </label>
              ))}
            </div>
            <p className={styles.textoMuted}>O número do pedido e os produtos aparecem sempre.</p>
          </section>

          {/* ---------- Histórico ---------- */}
          <section className={styles.cartao}>
            <div className={styles.cartaoCabecalho}>
              <h3 className={styles.cartaoTitulo}>Histórico de impressão</h3>
              <button type="button" className={styles.btnIcone} onClick={() => buscarDispositivosEHistorico()} title="Atualizar"><MdRefresh /></button>
            </div>
            {trabalhos.length === 0 ? (
              <p className={styles.textoMuted}>Nenhuma comanda ainda. Imprima um teste no passo 2.</p>
            ) : (
              <div className={styles.tabelaWrapper}>
                <table className={styles.tabela}>
                  <thead>
                    <tr>
                      <th>Quando</th>
                      <th>Comanda</th>
                      <th>Situação</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {trabalhos.map((trabalho) => (
                      <tr key={trabalho.id}>
                        <td className={styles.colunaMuted}>{formatarHora(trabalho.created_at)}</td>
                        <td>
                          {trabalho.tipo === 'teste' ? 'Comanda de teste' : `Pedido #${trabalho.numero_pedido ?? '—'}`}
                          {trabalho.tipo === 'reimpressao' && <span className={styles.textoMuted}> · reimpressão</span>}
                          {trabalho.origem_pedido && <span className={styles.textoMuted}> · {ROTULO_ORIGEM[trabalho.origem_pedido] || trabalho.origem_pedido}</span>}
                        </td>
                        <td>
                          <span className={`${styles.status} ${styles[`status_${trabalho.status}`] || ''}`}>{ROTULO_STATUS[trabalho.status] || trabalho.status}</span>
                          {trabalho.status === 'incerto' && <div className={styles.detalheErro}>O aparelho reiniciou durante a impressão. Confira se a comanda saiu; se não, reimprima.</div>}
                          {trabalho.status !== 'impresso' && trabalho.status !== 'incerto' && trabalho.erro_codigo && (
                            <div className={styles.detalheErro}>{ROTULO_ERRO[trabalho.erro_codigo] || trabalho.erro || 'Erro ao imprimir.'}</div>
                          )}
                          {trabalho.status === 'expirado' && !trabalho.erro_codigo && <div className={styles.detalheErro}>Nenhum aparelho estava ligado para imprimir.</div>}
                        </td>
                        <td className={styles.colunaAcoes}>
                          {trabalho.status !== 'pendente' && trabalho.status !== 'reservado' && (
                            <button type="button" className={styles.btnSecundario} onClick={() => reimprimir(trabalho)}>
                              <MdReplay /> <span>Reimprimir</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export default PainelImpressao;
